import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Row, Col, Card, Statistic, Progress, Spin } from 'antd'
import {
  FileTextOutlined,
  ShoppingCartOutlined,
  DollarOutlined,
  FileProtectOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons'
import numeral from 'numeral'
import { getInsights } from '../../../../HttpRequests/insights'
import { getSubOrders } from '../../../../HttpRequests/subOrders'
import styles from './styles.css'

const formatCurrency = (value) => `£${numeral(value).format('0,0.00')}`
const formatPercent = (value) => `${numeral(value).format('0')}%`

const Summary = ({
  project,
  projectTargetAmounts,
  fetchingProjectTargetAmounts,
  componentDidMount,
  componentWillUnmount,
}) => {

  const [timesheetTotal, setTimesheetTotal] = useState(0)
  const [subOrderTotal, setSubOrderTotal] = useState(0)
  const [fetchingLabourSplit, setFetchingLabourSplit] = useState(false)

  useEffect(() => {
    componentDidMount()
    fetchLabourSplit()
    return componentWillUnmount
  }, [])

  const fetchLabourSplit = async () => {
    setFetchingLabourSplit(true)
    try {
      // Fetch timesheet totals
      const timesheetParams = {
        model: 'Timesheet',
        field: 'project_id',
        filter_project_id: project.id,
      }
      const timesheetResponse = await getInsights({ params: timesheetParams })
      const timesheetData = timesheetResponse.data.data || []
      const timesheetSum = timesheetData.reduce((sum, record) => sum + parseFloat(record.total_cost || 0), 0)
      setTimesheetTotal(timesheetSum)

      // Fetch sub order totals
      const subOrderParams = {
        'filter[project_id]': project.id,
      }
      const subOrderResponse = await getSubOrders({ params: subOrderParams })
      const subOrderData = subOrderResponse.data || []
      const subOrderSum = subOrderData.reduce((sum, order) => sum + parseFloat(order.set_amount || 0), 0)
      setSubOrderTotal(subOrderSum)
    } catch (error) {
      console.error('Error fetching labour split:', error)
    } finally {
      setFetchingLabourSplit(false)
    }
  }

  const estimatesCount = (project.estimates && project.estimates.length) || 0
  const ordersCount = (project.orders && project.orders.length) || 0
  const valuationsCount = (project.valuations && project.valuations.length) || 0
  const invoicesCount = (project.invoices && project.invoices.length) || 0

  // Calculate overall valuation totals
  const valuationTotalValue = (project.valuations || []).reduce((sum, val) => {
    return sum + (Number(val.complete_total) || 0)
  }, 0)
  const valuationTotalCertificate = (project.valuations || []).reduce((sum, val) => {
    return sum + (Number(val.certificate_amount) || 0)
  }, 0)

  // Calculate overall order totals
  const orderTotalTarget = (project.orders || []).reduce((sum, order) => {
    return sum + (Number(order.target_total) || 0)
  }, 0)
  const orderTotalCoverage = (project.orders || []).reduce((sum, order) => {
    return sum + (order.has_partial_coverage ? (Number(order.partial_coverage_amount) || 0) : 0)
  }, 0)

  const valueTotal = Number(project.value_total) || 0
  const valueCompleted = Number(project.value_completed) || 0
  const valueRemaining = Number(project.value_remaining) || 0
  const completionPercent = valueTotal > 0 ? (valueCompleted / valueTotal) * 100 : 0

  const salesTotal = Number(project.sales_total) || 0
  const salesLabour = Number(project.sales_labour) || 0
  const salesMaterials = Number(project.sales_materials) || 0
  const salesEquipment = Number(project.sales_equipment) || 0

  const clientCompletedAmount = Number((projectTargetAmounts && projectTargetAmounts.client_completed_amount) || 0)
  const profit = clientCompletedAmount - salesTotal

  const targetCostTotal = Number((projectTargetAmounts && projectTargetAmounts.target_cost_total_amount) || 0)
  const targetLabourTotal = Number((projectTargetAmounts && projectTargetAmounts.target_labour_total_amount) || 0)
  const targetOtherTotal = Number((projectTargetAmounts && projectTargetAmounts.target_other_total_amount) || 0)
  const targetProfitTotal = Number((projectTargetAmounts && projectTargetAmounts.target_profit_total_amount) || 0)

  const labourPercent = salesTotal > 0 ? (salesLabour / salesTotal) * 100 : 0
  const materialsPercent = salesTotal > 0 ? (salesMaterials / salesTotal) * 100 : 0
  const equipmentPercent = salesTotal > 0 ? (salesEquipment / salesTotal) * 100 : 0

  const costVsTargetPercent = targetCostTotal > 0 ? (salesTotal / targetCostTotal) * 100 : 0
  const labourVsTargetPercent = targetLabourTotal > 0 ? (salesLabour / targetLabourTotal) * 100 : 0

  return (
    <div className={styles.wrapper}>

      {/* Statistics Cards */}
      <Row gutter={16} className={styles.statsRow}>
        <Col span={6}>
          <Card className={styles.statCard}>
            <Statistic
              title="Estimates"
              value={estimatesCount}
              prefix={<FileTextOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card className={styles.statCard}>
            <Statistic
              title="Orders"
              value={ordersCount}
              prefix={<ShoppingCartOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card className={styles.statCard}>
            <Statistic
              title="Valuations"
              value={valuationsCount}
              prefix={<DollarOutlined />}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card className={styles.statCard}>
            <Statistic
              title="Invoices"
              value={invoicesCount}
              prefix={<FileProtectOutlined />}
              valueStyle={{ color: '#fa8c16' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Valuation and Order Totals */}
      <Row gutter={16} className={styles.sectionRow}>
        <Col span={12}>
          <Card title="Valuation Totals" className={styles.card}>
            <div className={styles.targetComparison}>
              <div className={styles.targetItem}>
                <div className={styles.targetLabel}>Total Value</div>
                <div className={styles.targetValues}>
                  <span className={styles.targetActual}>{formatCurrency(valuationTotalValue)}</span>
                </div>
              </div>
              <div className={styles.targetItem}>
                <div className={styles.targetLabel}>Total Certificate Amount</div>
                <div className={styles.targetValues}>
                  <span className={styles.targetActual}>{formatCurrency(valuationTotalCertificate)}</span>
                </div>
              </div>
            </div>
          </Card>
        </Col>

        <Col span={12}>
          <Card title="Order Totals" className={styles.card}>
            <div className={styles.targetComparison}>
              <div className={styles.targetItem}>
                <div className={styles.targetLabel}>Total Target</div>
                <div className={styles.targetValues}>
                  <span className={styles.targetActual}>{formatCurrency(orderTotalTarget)}</span>
                </div>
              </div>
              <div className={styles.targetItem}>
                <div className={styles.targetLabel}>Total Coverage</div>
                <div className={styles.targetValues}>
                  <span className={styles.targetActual}>{formatCurrency(orderTotalCoverage)}</span>
                </div>
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Project Completion */}
      <Row gutter={16} className={styles.sectionRow}>
        <Col span={12}>
          <Card title="Project Completion" className={styles.card}>
            <div className={styles.progressSection}>
              <Progress
                type="circle"
                percent={Math.round(completionPercent)}
                strokeColor={{
                  '0%': '#108ee9',
                  '100%': '#87d068',
                }}
                size={120}
              />
              <div className={styles.progressDetails}>
                <div className={styles.progressItem}>
                  <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 8 }} />
                  <span className={styles.progressLabel}>Completed:</span>
                  <span className={styles.progressValue}>{formatCurrency(valueCompleted)}</span>
                </div>
                <div className={styles.progressItem}>
                  <ClockCircleOutlined style={{ color: '#faad14', marginRight: 8 }} />
                  <span className={styles.progressLabel}>Remaining:</span>
                  <span className={styles.progressValue}>{formatCurrency(valueRemaining)}</span>
                </div>
                <div className={styles.progressItem}>
                  <DollarOutlined style={{ color: '#1890ff', marginRight: 8 }} />
                  <span className={styles.progressLabel}>Total Value:</span>
                  <span className={styles.progressValue}><strong>{formatCurrency(valueTotal)}</strong></span>
                </div>
              </div>
            </div>
          </Card>
        </Col>

        <Col span={12}>
          <Card title="Works Breakdown" className={styles.card}>
            <div className={styles.worksBreakdown}>
              <div className={styles.worksItem}>
                <div className={styles.worksLabel}>New Works</div>
                <div className={styles.worksValues}>
                  <span>{formatCurrency(project.value_completed_new_works)}</span>
                  <span className={styles.worksSeparator}>/</span>
                  <span>{formatCurrency(project.value_total_new_works)}</span>
                </div>
                <Progress
                  percent={project.value_total_new_works > 0 ? Math.round((project.value_completed_new_works / project.value_total_new_works) * 100) : 0}
                  strokeColor="#1890ff"
                  size="small"
                />
              </div>
              <div className={styles.worksItem}>
                <div className={styles.worksLabel}>Extra Works</div>
                <div className={styles.worksValues}>
                  <span>{formatCurrency(project.value_completed_extra_works)}</span>
                  <span className={styles.worksSeparator}>/</span>
                  <span>{formatCurrency(project.value_total_extra_works)}</span>
                </div>
                <Progress
                  percent={project.value_total_extra_works > 0 ? Math.round((project.value_completed_extra_works / project.value_total_extra_works) * 100) : 0}
                  strokeColor="#722ed1"
                  size="small"
                />
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Cost Analysis - Row 1 */}
      <Spin spinning={fetchingProjectTargetAmounts}>
        <Row gutter={16} className={styles.sectionRow}>
          <Col span={12}>
            <Card title="Actual Costs" className={styles.card}>
              <div className={styles.costBreakdown}>
                <div className={styles.costTotal}>
                  <span className={styles.costLabel}>Total Cost</span>
                  <span className={styles.costValue}><strong>{formatCurrency(salesTotal)}</strong></span>
                </div>
                <div className={styles.costItem}>
                  <div className={styles.costItemHeader}>
                    <span>Labour</span>
                    <span>{formatCurrency(salesLabour)}</span>
                  </div>
                  <Progress percent={Math.round(labourPercent)} strokeColor="#1890ff" size="small" />
                </div>
                <div className={styles.costItem}>
                  <div className={styles.costItemHeader}>
                    <span>Materials</span>
                    <span>{formatCurrency(salesMaterials)}</span>
                  </div>
                  <Progress percent={Math.round(materialsPercent)} strokeColor="#52c41a" size="small" />
                </div>
                <div className={styles.costItem}>
                  <div className={styles.costItemHeader}>
                    <span>Equipment</span>
                    <span>{formatCurrency(salesEquipment)}</span>
                  </div>
                  <Progress percent={Math.round(equipmentPercent)} strokeColor="#fa8c16" size="small" />
                </div>
              </div>
            </Card>
          </Col>

          <Col span={12}>
            <Card title="Target vs Actual" className={styles.card}>
              <div className={styles.targetComparison}>
                <div className={styles.targetItem}>
                  <div className={styles.targetLabel}>Total Cost</div>
                  <div className={styles.targetValues}>
                    <span className={styles.targetActual}>{formatCurrency(salesTotal)}</span>
                    <span className={styles.targetSeparator}>of</span>
                    <span className={styles.targetTarget}>{formatCurrency(targetCostTotal)}</span>
                  </div>
                  <Progress
                    percent={Math.min(Math.round(costVsTargetPercent), 100)}
                    strokeColor={costVsTargetPercent > 100 ? '#ff4d4f' : '#52c41a'}
                    size="small"
                  />
                  <div className={styles.targetPercent}>
                    <span className={costVsTargetPercent > 100 ? styles.overBudget : styles.underBudget}>
                      {formatPercent(costVsTargetPercent)}
                    </span>
                  </div>
                </div>
                <div className={styles.targetItem}>
                  <div className={styles.targetLabel}>Labour Cost</div>
                  <div className={styles.targetValues}>
                    <span className={styles.targetActual}>{formatCurrency(salesLabour)}</span>
                    <span className={styles.targetSeparator}>of</span>
                    <span className={styles.targetTarget}>{formatCurrency(targetLabourTotal)}</span>
                  </div>
                  <Progress
                    percent={Math.min(Math.round(labourVsTargetPercent), 100)}
                    strokeColor={labourVsTargetPercent > 100 ? '#ff4d4f' : '#52c41a'}
                    size="small"
                  />
                  <div className={styles.targetPercent}>
                    <span className={labourVsTargetPercent > 100 ? styles.overBudget : styles.underBudget}>
                      {formatPercent(labourVsTargetPercent)}
                    </span>
                  </div>
                </div>
              </div>
            </Card>
          </Col>
        </Row>
      </Spin>

      {/* Cost Analysis - Row 2 */}
      <Spin spinning={fetchingProjectTargetAmounts || fetchingLabourSplit}>
        <Row gutter={16} className={styles.sectionRow}>
          <Col span={12}>
            <Card title="Profit Analysis" className={styles.card}>
              <div className={styles.profitSection}>
                <div className={styles.profitMain}>
                  <div className={styles.profitLabel}>Actual Profit</div>
                  <div className={profit >= 0 ? styles.profitPositive : styles.profitNegative}>
                    {formatCurrency(profit)}
                  </div>
                </div>
                <div className={styles.profitTarget}>
                  <div className={styles.profitTargetLabel}>Target Profit</div>
                  <div className={styles.profitTargetValue}>{formatCurrency(targetProfitTotal)}</div>
                </div>
                {targetProfitTotal > 0 && (
                  <div className={styles.profitProgress}>
                    <Progress
                      percent={Math.min(Math.round((profit / targetProfitTotal) * 100), 100)}
                      strokeColor={profit >= targetProfitTotal ? '#52c41a' : '#faad14'}
                      size="small"
                    />
                  </div>
                )}
                <div className={styles.manDays}>
                  <div className={styles.manDaysLabel}>Man Days</div>
                  <div className={styles.manDaysValues}>
                    <span>Actual: <strong>{numeral((projectTargetAmounts && projectTargetAmounts.man_days_actual_amount) || 0).format('0,0.00')}</strong></span>
                    <span>Target: <strong>{numeral((projectTargetAmounts && projectTargetAmounts.man_days_total_amount) || 0).format('0,0.00')}</strong></span>
                  </div>
                </div>
              </div>
            </Card>
          </Col>

          <Col span={12}>
            <Card title="Labour Cost Split" className={styles.card}>
              <div className={styles.targetComparison}>
                <div className={styles.targetItem}>
                  <div className={styles.targetLabel}>Timesheets</div>
                  <div className={styles.targetValues}>
                    <span className={styles.targetActual}>{formatCurrency(timesheetTotal)}</span>
                  </div>
                  <Progress
                    percent={(timesheetTotal + subOrderTotal) > 0 ? Math.round((timesheetTotal / (timesheetTotal + subOrderTotal)) * 100) : 0}
                    strokeColor="#1890ff"
                    size="small"
                  />
                  <div className={styles.targetPercent}>
                    <span style={{ color: '#1890ff', fontWeight: 500 }}>
                      {(timesheetTotal + subOrderTotal) > 0 ? formatPercent((timesheetTotal / (timesheetTotal + subOrderTotal)) * 100) : '0%'}
                    </span>
                  </div>
                </div>
                <div className={styles.targetItem}>
                  <div className={styles.targetLabel}>Sub Orders</div>
                  <div className={styles.targetValues}>
                    <span className={styles.targetActual}>{formatCurrency(subOrderTotal)}</span>
                  </div>
                  <Progress
                    percent={(timesheetTotal + subOrderTotal) > 0 ? Math.round((subOrderTotal / (timesheetTotal + subOrderTotal)) * 100) : 0}
                    strokeColor="#52c41a"
                    size="small"
                  />
                  <div className={styles.targetPercent}>
                    <span style={{ color: '#52c41a', fontWeight: 500 }}>
                      {(timesheetTotal + subOrderTotal) > 0 ? formatPercent((subOrderTotal / (timesheetTotal + subOrderTotal)) * 100) : '0%'}
                    </span>
                  </div>
                </div>
              </div>
            </Card>
          </Col>
        </Row>
      </Spin>

    </div>
  )
}

Summary.propTypes = {
  project: PropTypes.object.isRequired,
  projectTargetAmounts: PropTypes.object.isRequired,
  fetchingProjectTargetAmounts: PropTypes.bool.isRequired,
  componentDidMount: PropTypes.func.isRequired,
  componentWillUnmount: PropTypes.func.isRequired,
}

export default Summary
